import React, { useState } from "react";
import { Table, Tag, Select, Input, Button, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useGetAllFeedbackCrossOrg, useUpdateFeedback } from "~/services/feedback";
import { TFeedback } from "~/types/feedback";

const STATUS_COLORS: Record<string, string> = {
  OPEN: "red",
  IN_PROGRESS: "orange",
  COMPLETED: "green",
};

export const FeedbackTab: React.FC = () => {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editComment, setEditComment] = useState("");
  const [editStatus, setEditStatus] = useState<string>("");

  const { data, isLoading } = useGetAllFeedbackCrossOrg({
    page,
    pageSize: 20,
    status: statusFilter as any,
  });

  const updateMutation = useUpdateFeedback();

  const handleSave = async (id: string) => {
    try {
      await updateMutation.mutateAsync({
        id,
        data: {
          status: editStatus as any,
          adminComments: editComment,
        },
      });
      message.success("Feedback updated");
      setEditingId(null);
    } catch {
      message.error("Failed to update feedback");
    }
  };

  const startEditing = (record: TFeedback) => {
    setEditingId(record.id);
    setEditStatus(record.status);
    setEditComment(record.adminComments || "");
  };

  const items = (data as any)?.data?.items || [];
  const meta = (data as any)?.data?.meta;

  const columns: ColumnsType<TFeedback> = [
    {
      title: "Organization",
      key: "organization",
      width: "15%",
      render: (_, record) => (
        <span className="font-medium">
          {record.organization?.name || "—"}
        </span>
      ),
    },
    {
      title: "User",
      key: "user",
      width: "15%",
      render: (_, record) => (
        <div>
          <div className="font-medium">
            {record.user?.firstName} {record.user?.lastName}
          </div>
          <div className="text-xs text-gray-400">{record.user?.role}</div>
        </div>
      ),
    },
    {
      title: "Feedback",
      dataIndex: "description",
      key: "description",
      width: "30%",
      render: (text) => (
        <div className="max-w-md truncate" title={text}>
          {text}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "12%",
      render: (status, record) =>
        editingId === record.id ? (
          <Select
            size="small"
            value={editStatus}
            onChange={setEditStatus}
            style={{ width: 120 }}
            options={[
              { label: "Open", value: "OPEN" },
              { label: "In Progress", value: "IN_PROGRESS" },
              { label: "Completed", value: "COMPLETED" },
            ]}
          />
        ) : (
          <Tag color={STATUS_COLORS[status]}>{status.replace("_", " ")}</Tag>
        ),
    },
    {
      title: "Admin Comments",
      dataIndex: "adminComments",
      key: "adminComments",
      width: "18%",
      render: (text, record) =>
        editingId === record.id ? (
          <Input.TextArea
            size="small"
            rows={2}
            value={editComment}
            onChange={(e) => setEditComment(e.target.value)}
            placeholder="Add comment..."
          />
        ) : (
          <span className="text-gray-500 text-sm">{text || "—"}</span>
        ),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: "10%",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "",
      key: "actions",
      width: "10%",
      render: (_, record) =>
        editingId === record.id ? (
          <div className="flex gap-1">
            <Button size="small" type="primary" loading={updateMutation.isPending} onClick={() => handleSave(record.id)}>
              Save
            </Button>
            <Button size="small" onClick={() => setEditingId(null)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button size="small" onClick={() => startEditing(record)}>
            Respond
          </Button>
        ),
    },
  ];

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <p className="text-gray-500">
          Feedback from all tenant organizations
        </p>
        <Select
          allowClear
          placeholder="Filter by status"
          style={{ width: 160 }}
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { label: "Open", value: "OPEN" },
            { label: "In Progress", value: "IN_PROGRESS" },
            { label: "Completed", value: "COMPLETED" },
          ]}
        />
      </div>

      <Table
        columns={columns}
        dataSource={items}
        rowKey="id"
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: 20,
          total: meta?.totalItems || 0,
          onChange: setPage,
          showTotal: (total) => `${total} feedback items`,
        }}
        size="small"
      />
    </div>
  );
};
